import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CATEGORIA_PRODUTO,
  type CategoriaProduto,
  type ProdutoNaTabela,
} from '@arena/shared/schemas';
import { dinheiro } from '../agenda/formatos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { estoqueApi } from '../api/estoque.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeTabela } from '../ui/estados.jsx';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Formulario, Nota, Tabela, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { margemNaTabela, NOME_DO_PRODUTO, quandoEntrou } from './formatos.js';
import {
  edicaoDoFormulario,
  formularioDoProduto,
  formularioVazio,
  movimentoComCusto,
  origemDoMovimento,
  problemasDoProduto,
  produtoNovoDoFormulario,
  quantidadeDoMovimento,
  rotuloDoMovimento,
  type FormularioDeProduto,
} from './produto.js';

/** Linhas por página do histórico; "Carregar mais" traz a seguinte. */
const MOVIMENTOS_POR_PAGINA = 25;

/**
 * `#m-produto` — o cadastro e o detalhe de um produto.
 *
 * Sem `produtoId` é o cadastro (`estoque:criar`); com ele, o detalhe: os
 * dados (editáveis com `estoque:editar`, e a checagem do front é só
 * cosmética — quem decide é o servidor), ativo/inativo e o histórico de
 * movimentos, com rótulo e origem.
 *
 * O CUSTO NÃO SE EDITA aqui. Ele muda só por entrada de mercadoria (é o médio
 * ponderado): o campo existe só no cadastro — onde a primeira compra o
 * define, como no "Produto novo" da entrada — e a edição nunca o envia. Para
 * quem recebeu o custo, o detalhe o mostra como leitura. A coluna de custo
 * do histórico existe quando o servidor a mandou.
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */
export function ModalDeProduto({
  produtoId,
  onFechar,
}: {
  produtoId: string | null;
  onFechar: () => void;
}) {
  if (produtoId === null) return <CadastroDeProduto onFechar={onFechar} />;
  return <DetalheDoProduto produtoId={produtoId} onFechar={onFechar} />;
}

// ---------------------------------------------------------------------
// Cadastro
// ---------------------------------------------------------------------

function CadastroDeProduto({ onFechar }: { onFechar: () => void }) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const [form, setForm] = useState<FormularioDeProduto>(formularioVazio);
  const [tentou, setTentou] = useState(false);
  const [erro, setErro] = useState<string | undefined>();

  const problemas = tentou ? problemasDoProduto(form, 'novo') : {};
  const criar = useMutation({
    mutationFn: () => {
      const pedido = produtoNovoDoFormulario(form);
      if (!pedido) return Promise.reject(new Error('Confira os campos do produto.'));
      return estoqueApi.criarProduto(pedido);
    },
    onSuccess: async (criado) => {
      await cliente.invalidateQueries({ queryKey: ['estoque'] });
      avisar(`${criado.nome} cadastrado. O estoque começa em zero: registre uma entrada.`);
      onFechar();
    },
    onError: (e: unknown) => setErro(mensagemDoErro(e)),
  });

  function salvar() {
    setErro(undefined);
    setTentou(true);
    if (produtoNovoDoFormulario(form) === null) return;
    criar.mutate();
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      titulo="Novo produto"
      descricao="Cadastre o produto; o estoque entra pela entrada de mercadoria."
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={salvar} disabled={criar.isPending}>
            {criar.isPending ? 'Salvando…' : 'Cadastrar produto'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        {erro && <Nota>{erro}</Nota>}
        <CamposDoProduto form={form} onMudar={setForm} problemas={problemas} comCusto podeEditar />
      </CorpoDeModal>
    </Modal>
  );
}

// ---------------------------------------------------------------------
// Detalhe
// ---------------------------------------------------------------------

function DetalheDoProduto({ produtoId, onFechar }: { produtoId: string; onFechar: () => void }) {
  const produto = useQuery({
    queryKey: ['estoque', 'produto', produtoId],
    queryFn: () => estoqueApi.produto(produtoId),
  });

  if (produto.isPending || produto.isError) {
    return (
      <Modal
        aberto
        onFechar={onFechar}
        titulo="Produto"
        descricao="Dados e histórico de movimentos."
        rodape={
          <Botao variante="fantasma" onClick={onFechar}>
            Fechar
          </Botao>
        }
      >
        <CorpoDeModal>
          {produto.isPending ? (
            <EsqueletoDeTabela colunas={3} />
          ) : (
            <Erro
              detalhe={mensagemDoErro(produto.error)}
              onTentarDeNovo={() => void produto.refetch()}
            />
          )}
        </CorpoDeModal>
      </Modal>
    );
  }
  // O formulário nasce do produto carregado e NÃO se refaz a cada nova leitura:
  // o que a pessoa digitou não pode ser trocado por baixo dela.
  return <EdicaoDoProduto produto={produto.data} onFechar={onFechar} />;
}

function EdicaoDoProduto({
  produto,
  onFechar,
}: {
  produto: ProdutoNaTabela;
  onFechar: () => void;
}) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();
  const podeEditar = pode('estoque', 'editar');
  // O retrato de quando o modal abriu: o formulário nasceu dele, e é contra ele
  // que se decide "o que a pessoa mudou". Comparar com `produto` (a leitura viva,
  // que um refetch renova) mandaria o preço velho do formulário por cima do que
  // outra sessão acabou de gravar, só porque o vivo deixou de ser igual a ele.
  const [retrato] = useState(() => produto);
  const [form, setForm] = useState<FormularioDeProduto>(() => formularioDoProduto(retrato));
  const [erro, setErro] = useState<string | undefined>();

  const problemas = problemasDoProduto(form, 'edicao');
  const pedido = edicaoDoFormulario(form, retrato);
  const mudou = Object.keys(pedido).length > 0;

  const salvar = useMutation({
    mutationFn: () => estoqueApi.editarProduto(produto.id, pedido),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['estoque'] });
      avisar('Produto atualizado.');
      onFechar();
    },
    onError: (e: unknown) => setErro(mensagemDoErro(e)),
  });

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="larga"
      titulo={produto.nome}
      descricao={`${produto.saldo} em estoque · mínimo ${produto.estoqueMinimo}.`}
      rodape={
        podeEditar ? (
          <>
            <Botao variante="fantasma" onClick={onFechar}>
              Cancelar
            </Botao>
            <Botao
              onClick={() => {
                setErro(undefined);
                salvar.mutate();
              }}
              disabled={!mudou || Object.keys(problemas).length > 0 || salvar.isPending}
            >
              {salvar.isPending ? 'Salvando…' : 'Salvar alterações'}
            </Botao>
          </>
        ) : (
          <Botao variante="fantasma" onClick={onFechar}>
            Fechar
          </Botao>
        )
      }
    >
      <CorpoDeModal>
        {erro && <Nota>{erro}</Nota>}
        <CamposDoProduto
          form={form}
          onMudar={setForm}
          problemas={problemas}
          comCusto={false}
          podeEditar={podeEditar}
        />
        {typeof produto.custo === 'number' && (
          <Nota>
            Custo atual <b>{dinheiro(produto.custo)}</b> · margem <b>{margemNaTabela(produto)}</b>.
            O custo muda só por entrada de mercadoria (é o médio ponderado das compras).
          </Nota>
        )}
        <HistoricoDoProduto produtoId={produto.id} />
      </CorpoDeModal>
    </Modal>
  );
}

// ---------------------------------------------------------------------
// Os campos, e o histórico
// ---------------------------------------------------------------------

function CamposDoProduto({
  form,
  onMudar,
  problemas,
  comCusto,
  podeEditar,
}: {
  form: FormularioDeProduto;
  onMudar: (form: FormularioDeProduto) => void;
  problemas: ReturnType<typeof problemasDoProduto>;
  /** O custo só existe no cadastro: a edição nunca o envia. */
  comCusto: boolean;
  podeEditar: boolean;
}) {
  const campo = <K extends keyof FormularioDeProduto>(chave: K, valor: FormularioDeProduto[K]) =>
    onMudar({ ...form, [chave]: valor });

  return (
    <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
      <Campo rotulo="Nome" erro={problemas.nome}>
        <input
          value={form.nome}
          maxLength={NOME_DO_PRODUTO.maximo}
          aria-label="Nome do produto"
          disabled={!podeEditar}
          onChange={(e) => campo('nome', e.target.value)}
          placeholder="Ex.: Coca-Cola Zero 350 ml"
        />
      </Campo>
      <Campo rotulo="Categoria">
        <select
          value={form.categoria}
          aria-label="Categoria do produto"
          disabled={!podeEditar}
          onChange={(e) => campo('categoria', e.target.value as CategoriaProduto)}
        >
          {CATEGORIA_PRODUTO.map((opcao) => (
            <option key={opcao} value={opcao}>
              {opcao}
            </option>
          ))}
        </select>
      </Campo>
      <Campo rotulo="Preço de venda" erro={problemas.preco}>
        <input
          value={form.preco}
          inputMode="decimal"
          aria-label="Preço de venda"
          disabled={!podeEditar}
          onChange={(e) => campo('preco', e.target.value)}
        />
      </Campo>
      <Campo rotulo="Estoque mínimo" erro={problemas.minimo}>
        <input
          type="number"
          min={0}
          value={form.minimo}
          aria-label="Estoque mínimo"
          disabled={!podeEditar}
          onChange={(e) => campo('minimo', e.target.value)}
        />
      </Campo>
      {comCusto && (
        <Campo
          rotulo="Custo unitário"
          erro={problemas.custo}
          ajuda="O custo da primeira compra. Depois dele, o custo muda só por entrada de mercadoria."
        >
          <input
            value={form.custo}
            inputMode="decimal"
            aria-label="Custo unitário"
            onChange={(e) => campo('custo', e.target.value)}
          />
        </Campo>
      )}
      {!comCusto && podeEditar && (
        <Campo rotulo="Situação" ajuda="Produto inativo sai da busca da comanda; o histórico fica.">
          <select
            value={form.ativo ? 'ativo' : 'inativo'}
            aria-label="Situação do produto"
            onChange={(e) => campo('ativo', e.target.value === 'ativo')}
          >
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>
        </Campo>
      )}
    </Formulario>
  );
}

function HistoricoDoProduto({ produtoId }: { produtoId: string }) {
  const movimentos = useInfiniteQuery({
    queryKey: ['estoque', 'produto', produtoId, 'movimentos'],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      estoqueApi.movimentosDoProduto(produtoId, {
        limite: MOVIMENTOS_POR_PAGINA,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  });

  const itens = movimentos.data?.pages.flatMap((pagina) => pagina.itens) ?? [];
  const comCusto = movimentoComCusto(itens);

  return (
    <section aria-label="Histórico de movimentos">
      <h4 style={{ margin: '16px 0 8px' }}>Movimentos</h4>
      {movimentos.isPending && <EsqueletoDeTabela colunas={4} />}
      {movimentos.isError && (
        <Erro
          detalhe={mensagemDoErro(movimentos.error)}
          onTentarDeNovo={() => void movimentos.refetch()}
        />
      )}
      {movimentos.data && itens.length === 0 && <Vazio>Nenhum movimento ainda.</Vazio>}
      {itens.length > 0 && (
        <>
          <Tabela
            colunas={[
              'Data',
              'Movimento',
              'Origem',
              'Quantidade',
              ...(comCusto ? ['Custo unitário'] : []),
            ]}
          >
            {itens.map((movimento) => (
              <tr key={movimento.id}>
                <td>{quandoEntrou(movimento.criadoEm)}</td>
                <td>
                  {rotuloDoMovimento(movimento.tipo)}
                  {movimento.observacao && (
                    <small style={{ display: 'block', color: 'var(--dim)' }}>
                      {movimento.observacao}
                    </small>
                  )}
                </td>
                <td>{origemDoMovimento(movimento)}</td>
                <td className="num">{quantidadeDoMovimento(movimento.quantidade)}</td>
                {comCusto && (
                  <td className="num">
                    {movimento.custoUnitario === undefined
                      ? '—'
                      : dinheiro(movimento.custoUnitario)}
                  </td>
                )}
              </tr>
            ))}
          </Tabela>
          {movimentos.hasNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 2px' }}>
              <Botao
                variante="contorno"
                pequeno
                onClick={() => void movimentos.fetchNextPage()}
                disabled={movimentos.isFetchingNextPage}
              >
                {movimentos.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </Botao>
            </div>
          )}
        </>
      )}
    </section>
  );
}
